import { BaseTokenInfo, CHAIN_NAMESPACES, ChainNamespaceType, getEnvVariable, type ProviderConfig } from "@toruslabs/base-controllers";
import { BUILD_ENV_TYPE, CITADEL_SERVER_MAP, STORAGE_SERVER_MAP } from "@toruslabs/constants";
import {
  type AccountAbstractionMultiChainConfig,
  ARBITRUM_MAINNET_CHAIN_ID,
  ARBITRUM_SEPOLIA_CHAIN_ID,
  AVALANCHE_MAINNET_CHAIN_ID,
  BASE_CHAIN_ID,
  BASE_SEPOLIA_CHAIN_ID,
  BSC_MAINNET_CHAIN_ID,
  CELO_MAINNET_CHAIN_ID,
  EIP7702_SUPPORTED_SMART_ACCOUNT_TYPES,
  MAINNET_CHAIN_ID,
  OPTIMISM_MAINNET_CHAIN_ID,
  OPTIMISM_SEPOLIA_CHAIN_ID,
  POLYGON_CHAIN_ID,
  SEPOLIA_CHAIN_ID,
  SMART_ACCOUNT_EIP_STANDARD,
  SmartAccountType,
  toRpcConfig as toEvmRpcConfig,
} from "@toruslabs/ethereum-controllers";
import { get } from "@toruslabs/http-helpers";
import { add0x, remove0x } from "@toruslabs/metadata-helpers";
import { StorageManager } from "@toruslabs/session-manager";
import {
  CHAIN_IDS as SOLANA_CHAIN_IDS,
  type ChainId,
  type RPC_MAP_TYPE,
  SOLANA_NATIVE_MINT_ADDRESS,
  toRpcConfig as toSolanaRpcConfig,
} from "@toruslabs/solana-controllers";
import {
  applyWhiteLabelTheme,
  EthereumProviderError,
  rpcErrors,
  serializeJrpcError,
  WEB3AUTH_NETWORK_TYPE,
  WHITE_LABEL_THEME,
  WhiteLabelData,
} from "@web3auth/auth";
import bowser from "bowser";
import Color from "color";
import { cloneDeep } from "lodash-es";
import log from "loglevel";
import { getAddress, type Hex } from "viem";
import { privateKeyToAccount } from "viem/accounts";

import config from "../config";
import { analytics } from "./analytics";
import { LOCALE_EN, LOCALES, SOLANA_CAIP_CHAIN_MAP } from "./enums";

export const isMain = window.self === window.top;

export const checkSolanaChainNameSpace = (chainNamespace: ChainNamespaceType): boolean => chainNamespace === CHAIN_NAMESPACES.SOLANA;

export const checkNativeEthereumToken = (token: BaseTokenInfo): boolean => token.tokenAddress === "0x";

export const checkNativeSolanaToken = (token: BaseTokenInfo): boolean => token.tokenAddress === SOLANA_NATIVE_MINT_ADDRESS;

export const checkNativeToken = (token: BaseTokenInfo): boolean => checkNativeEthereumToken(token) || checkNativeSolanaToken(token);

export const infuraKey = getEnvVariable("VITE_APP_INFURA_PROJECT_KEY");
if (!infuraKey) throw new Error("Infura project key is not set");

const solanaMainnetRpc = getEnvVariable("VITE_APP_SOLANA_MAINNET_RPC");
if (!solanaMainnetRpc) throw new Error("Solana mainnet RPC is not set");

const solanaTestnetRpc = getEnvVariable("VITE_APP_SOLANA_TESTNET_RPC");
if (!solanaTestnetRpc) throw new Error("Solana testnet RPC is not set");

const solanaDevnetRpc = getEnvVariable("VITE_APP_SOLANA_DEVNET_RPC");
if (!solanaDevnetRpc) throw new Error("Solana devnet RPC is not set");

export const solanaRpcConfig: RPC_MAP_TYPE = {
  [SOLANA_CHAIN_IDS.SOLANA_MAINNET]: solanaMainnetRpc,
  [SOLANA_CHAIN_IDS.SOLANA_TESTNET]: solanaTestnetRpc,
  [SOLANA_CHAIN_IDS.SOLANA_DEVNET]: solanaDevnetRpc,
};

export const solanaSupportedNetworks = toSolanaRpcConfig(solanaRpcConfig);

export const evmSupportedNetworks = toEvmRpcConfig(infuraKey);

export const getIFrameOrigin = () => {
  const originHref = window.location.ancestorOrigins?.length > 0 ? window.location.ancestorOrigins[0] : document.referrer;
  if (!originHref) return originHref;
  const url = new URL(originHref);
  return url.origin;
};

export const getIFrameOriginObject = () => {
  try {
    const url = new URL(getIFrameOrigin());
    return { href: url.href, hostname: url.hostname };
  } catch {
    return { href: window.location.href, hostname: window.location.hostname };
  }
};

export const generateLoginId = (): Hex => StorageManager.generateRandomSessionKey();

export const storeDataOnStorageServer = async <T>(loginId: Hex, data: T, sessionNamespace?: string, timeout = 600): Promise<void> => {
  const sessionMgr = new StorageManager<T>({
    sessionId: loginId,
    sessionNamespace,
    sessionTime: timeout,
    sessionServerBaseUrl: STORAGE_SERVER_MAP[config.buildEnv as BUILD_ENV_TYPE],
  });
  await sessionMgr.createSession(data);
};

export const fetchDataFromStorageServer = async <T>(identifier: Hex, sessionNamespace?: string): Promise<T> => {
  const configManager = new StorageManager<T>({
    sessionId: identifier,
    sessionNamespace,
    sessionServerBaseUrl: STORAGE_SERVER_MAP[config.buildEnv as BUILD_ENV_TYPE],
  });
  const data = await configManager.authorizeSession();
  return data;
};

const shouldGateWhiteLabel = (whitelabel: WhiteLabelData): boolean => {
  if (!whitelabel || Object.keys(whitelabel).length === 0) return false;
  const localWhiteLabelData = cloneDeep(whitelabel || {});
  const whitelabelKeys = ["appName", "appUrl", "logoDark", "logoLight", "theme", "tncLink", "privacyPolicy"];
  // check if these keys are defined and if object its not an empty object.
  const isKeyPresent = (key: string): boolean => {
    const value = localWhiteLabelData[key as keyof WhiteLabelData];
    if (!value) return false;
    if (typeof value === "object" && Object.keys(value).length === 0) return false;
    return true;
  };

  return localWhiteLabelData && whitelabelKeys.some(isKeyPresent);
};

export const gateUser = async (options: { clientId: string; network: WEB3AUTH_NETWORK_TYPE; whiteLabel?: WhiteLabelData }) => {
  try {
    const isWhiteLabel = shouldGateWhiteLabel(options.whiteLabel ?? {});
    const url = new URL(`${CITADEL_SERVER_MAP[config.buildEnv as BUILD_ENV_TYPE]}/v1/signer/feature-access`);
    url.searchParams.append("client_id", options.clientId);
    url.searchParams.append("network", options.network);
    url.searchParams.append("is_wallet_service", "true");
    url.searchParams.append("enable_gating", "true");
    if (isWhiteLabel) {
      url.searchParams.append("is_whitelabel", String(isWhiteLabel));
    }
    const res = (await get(url.href)) as { metadata?: { team_id: number } };

    // update analytics
    analytics.setGlobalProperties({ team_id: res.metadata?.team_id });

    return {
      allowWalletService: true,
      errorMessage: "",
    };
  } catch (error) {
    if (error instanceof Response) {
      const gatingError = (await error.json()) as { error: string; metadata?: { team_id: number } };
      log.info("ERROR:", gatingError.error);

      // update analytics
      analytics.setGlobalProperties({ team_id: gatingError.metadata?.team_id });

      return {
        allowWalletService: false,
        errorMessage: gatingError.error,
      };
    }
    // network error
    log.error("ERROR:", error);
    return {
      allowWalletService: false,
      errorMessage: "Network error",
    };
  }
};

export const randomId = () => Math.random().toString(36).slice(2);

export const isMobileOrTablet = () => {
  const browser = bowser.getParser(window.navigator.userAgent);
  const platform = browser.getPlatform();
  return platform.type === bowser.PLATFORMS_MAP.tablet || platform.type === bowser.PLATFORMS_MAP.mobile;
};

export const broadcastChannelOptions = {
  type: "server", // (optional) enforce a type, oneOf['native', 'idb', 'localstorage', 'node']
  webWorkerSupport: false, // (optional) set this to false if you know that your channel will never be used in a WebWorker (increases performance)
};

export function waitForMs(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export class UserError extends Error {}

export function generateAddressFromPrivateKey(privKey: string) {
  const padded = remove0x(privKey).padStart(64, "0");
  return getAddress(privateKeyToAccount(add0x(padded)).address);
}

export function generatePubKeyFromPrivateKey(privKey: string): Hex {
  const padded = remove0x(privKey).padStart(64, "0");
  const account = privateKeyToAccount(add0x(padded));
  return `0x${account.publicKey.slice(4)}`;
}

export const getUserLanguage = (): string => {
  let userLanguage = (window.navigator as unknown as { userLanguage?: string }).userLanguage || window.navigator.language || "en-US";
  userLanguage = userLanguage.split("-")[0] || LOCALE_EN;
  const supportedLocales = LOCALES.map((locale) => locale.value);
  if (!supportedLocales.includes(userLanguage)) userLanguage = LOCALE_EN;
  return userLanguage;
};

export function promiseCreator<T>(): {
  resolve: ((value: T | PromiseLike<T>) => void) | null;
  reject: ((reason?: string) => void) | null;
  promise: Promise<T>;
} {
  let res: ((value: T | PromiseLike<T>) => void) | null = null;
  let rej: ((reason?: string) => void) | null = null;
  const promise = new Promise<T>((resolve, reject) => {
    res = resolve;
    rej = reject;
  });
  return {
    resolve: res,
    reject: rej,
    promise,
  };
}

export const getDate = () => {
  const currentDateTime = new Date();
  let hours = currentDateTime.getHours();
  const minutes = currentDateTime.getMinutes();
  const seconds = currentDateTime.getSeconds();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours %= 12;
  hours = hours || 12;
  return `${hours}:${minutes}:${seconds} ${ampm}`;
};

export const setThemeColors = (theme: WHITE_LABEL_THEME) => {
  const rootElement = document.querySelector(":root") as HTMLElement;
  applyWhiteLabelTheme(Color, rootElement, theme);
};

export const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export const isUnauthorizedError = (error: unknown) => {
  return error instanceof Response && error.status === 401;
};

export function getUniswapContractAddress(chainId: string): string {
  switch (chainId) {
    case SEPOLIA_CHAIN_ID:
      return "0x3bFA4769FB09eefC5a80d6E87c3B9C650f7Ae48E";
    case ARBITRUM_SEPOLIA_CHAIN_ID:
      return "0x101F443B4d1b059569D643917553c771E1b9663E";
    case OPTIMISM_SEPOLIA_CHAIN_ID:
      return "0x94cC0AaC535CCDB3C01d6787D6413C739ae12bc4";
    case BASE_SEPOLIA_CHAIN_ID:
      return "0x94cC0AaC535CCDB3C01d6787D6413C739ae12bc4";
    case BASE_CHAIN_ID:
      return "0x2626664c2603336E57B271c5C0b26F421741e481";
    case BSC_MAINNET_CHAIN_ID:
      return "0xB971eF87ede563556b2ED4b1C0b0019111Dd85d2";
    case CELO_MAINNET_CHAIN_ID:
      return "0x5615CDAb10dc425a742d643d949a7F474C01abc4";
    case AVALANCHE_MAINNET_CHAIN_ID:
      return "0xbb00FF08d01D300023C629E8fFfFcb65A5a578cE";
    default:
      return "0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45";
  }
}

export function isSwapAllowed(chainId: string): boolean {
  const allowList = [
    MAINNET_CHAIN_ID,
    ARBITRUM_MAINNET_CHAIN_ID,
    OPTIMISM_MAINNET_CHAIN_ID,
    POLYGON_CHAIN_ID,
    BASE_CHAIN_ID,
    BSC_MAINNET_CHAIN_ID,
    AVALANCHE_MAINNET_CHAIN_ID,
    CELO_MAINNET_CHAIN_ID,
    // Testnets
    SEPOLIA_CHAIN_ID,
  ];

  return allowList.includes(chainId);
}

export const MULTI_CHAIN_SUPPORTED_NETWORKS = {
  ...evmSupportedNetworks,
  ...solanaSupportedNetworks,
} as Record<string, ProviderConfig>;

// parse initial chain and supported chains
export const parseChains = (
  { chains, chainId }: { chains: ProviderConfig[]; chainId: string },
  accountAbstractionConfig?: AccountAbstractionMultiChainConfig
): { initialChain: ProviderConfig; supportedChains: ProviderConfig[] } => {
  // If no chains are provided, populate them to maintain compatibility with legacy torus-embed and solana-embed SDKs.
  if (!chains || chains?.length === 0) {
    if (evmSupportedNetworks[chainId]) {
      chains = Object.values(evmSupportedNetworks);
    } else if (solanaSupportedNetworks[chainId as ChainId]) {
      chains = Object.values(solanaSupportedNetworks);
    }
  }

  // filter out unsupported namespaces
  let supportedChains = chains.filter((x) => ([CHAIN_NAMESPACES.SOLANA, CHAIN_NAMESPACES.EIP155] as string[]).includes(x.chainNamespace));
  if (!supportedChains?.length) throw new Error("No chains provided");

  // determine the initial chain
  const builtInChain: ProviderConfig = { ...MULTI_CHAIN_SUPPORTED_NETWORKS[chainId], rpcTarget: undefined }; // remove rpcTarget to avoid reusing the same rpcTarget as the built-in chain
  const chain = supportedChains.find((x) => x.chainId === chainId);
  if (!chain) throw new Error(`Chain with id ${chainId} not found`);
  const initialChain = { ...builtInChain, ...chain, isTestnet: builtInChain?.isTestnet || chain.isTestnet || false };

  // When Account Abstraction is enabled and eipStandard is not 7702, filter out unsupported chains
  if (accountAbstractionConfig && accountAbstractionConfig.smartAccountEipStandard !== SMART_ACCOUNT_EIP_STANDARD.EIP_7702) {
    const aaSupportedChainIds = new Set(accountAbstractionConfig.chains.map((chain) => chain.chainId));
    // validate if the initial EVM chain is supported by AA
    if (initialChain.chainNamespace === CHAIN_NAMESPACES.EIP155 && !aaSupportedChainIds.has(chainId))
      throw new Error(`Account Abstraction is not supported for the chain ${chainId}`);
    // if AA chains are provided, filter out unsupported EVM chains
    supportedChains = supportedChains.filter((x) => x.chainNamespace === CHAIN_NAMESPACES.SOLANA || aaSupportedChainIds.has(x.chainId));
    if (!supportedChains?.length) throw new Error("Account Abstraction is enabled but none of the provided chains support it");
  }

  return { initialChain, supportedChains };
};

export const parseAccountAbstractionConfig = ({ accountAbstractionConfig }: { accountAbstractionConfig?: AccountAbstractionMultiChainConfig }) => {
  if (!accountAbstractionConfig) return null;

  // set default smartAccountEipStandard to 4337 if not provided
  if (!accountAbstractionConfig.smartAccountEipStandard) {
    accountAbstractionConfig.smartAccountEipStandard = SMART_ACCOUNT_EIP_STANDARD.ERC_4337;
  }
  const smartAccountEipStandard = accountAbstractionConfig.smartAccountEipStandard;

  // validate the accountAbstractionConfig
  if (!accountAbstractionConfig.smartAccountType) throw new Error("Account Abstraction is enabled but smartAccountType is not provided");
  if (smartAccountEipStandard === SMART_ACCOUNT_EIP_STANDARD.EIP_7702) {
    const isAllowed7702SmartAccountType = (EIP7702_SUPPORTED_SMART_ACCOUNT_TYPES as unknown as SmartAccountType[]).includes(
      accountAbstractionConfig.smartAccountType
    );
    if (!isAllowed7702SmartAccountType)
      throw new Error(
        `Smart account type "${accountAbstractionConfig.smartAccountType}" does not support EIP-7702. Supported: ${EIP7702_SUPPORTED_SMART_ACCOUNT_TYPES.join(", ")}.`
      );
  } else {
    // filter out chains that don't have a bundler URL
    accountAbstractionConfig.chains = accountAbstractionConfig.chains.filter((aaChainConfig) => aaChainConfig.bundlerConfig?.url);
    // AA chains are required for 4337 only
    if (accountAbstractionConfig.chains.length === 0) throw new Error("Account Abstraction is enabled but config per chain is not provided");
  }

  return accountAbstractionConfig;
};

export const getHostname = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
};

export const getCaipChainId = (chain: Pick<ProviderConfig, "chainNamespace" | "chainId">) => {
  if (chain.chainNamespace === CHAIN_NAMESPACES.EIP155) {
    return `${chain.chainNamespace}:${parseInt(chain.chainId, 16)}`;
  }
  if (chain.chainNamespace === CHAIN_NAMESPACES.SOLANA) {
    return `${chain.chainNamespace}:${SOLANA_CAIP_CHAIN_MAP[chain.chainId]}`;
  }

  // for other chain namespaces, we just return the chainId as is
  return `${chain.chainNamespace}:${chain.chainId}`;
};

export function formatSwitchChainError(error: unknown, targetChainId: string, targetChainNamespace: ChainNamespaceType): Error {
  if (targetChainNamespace === CHAIN_NAMESPACES.EIP155) {
    return formatEvmSwitchChainError(error, targetChainId);
  }
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Format the error for Ethereum switch chain RPC call, `wallet_switchEthereumChain`
 * @param error - The error to format
 * @param targetChainId - The target chain ID
 * @returns The formatted error
 */
export function formatEvmSwitchChainError(error: unknown, targetChainId: string): Error {
  const serializedError = serializeJrpcError(error, {
    shouldIncludeStack: false,
    shouldPreserveMessage: true,
    fallbackError: rpcErrors.internal({
      message: error instanceof Error ? error.message : `Failed to switch to chain ${targetChainId}`,
      data: { cause: error, chainId: targetChainId },
    }),
  });
  const message = error instanceof Error ? error.message : `Failed to switch to chain ${targetChainId}`;
  const data =
    serializedError.data && typeof serializedError.data === "object"
      ? { ...serializedError.data, chainId: targetChainId }
      : { cause: serializedError.data, chainId: targetChainId };

  const messageLower = message.toLowerCase();
  if (
    messageLower.includes("unsupported network") ||
    messageLower.includes("unsupported chain") ||
    messageLower.includes("unsupported chain namespace")
  ) {
    return new EthereumProviderError(4200, message, data);
  }

  if (messageLower.includes("unauthorized")) {
    return new EthereumProviderError(4100, message, data);
  }

  if (typeof serializedError.code === "number" && (serializedError.code >= 1000 || serializedError.code === 4001)) {
    return new EthereumProviderError(serializedError.code, message, data);
  }

  return rpcErrors.internal({
    message,
    data,
  });
}
